import { FC } from 'react'

import { Button, useNotifications } from '@sequencehq/core-components'
import {
  DashboardApi20240730,
  dashboard20240730Client
} from '@sequencehq/api/dashboard/v20240730'

import { ModalRequestSignaturesContent } from 'Cube/view/layouts/quote/QuoteEditorSidebar/modals/ModalRequestSignaturesContent'
import { usePermissions } from 'lib/rbac'

// Signature requests can only be sent from a PUBLISHED quote, so for every
// other status the button is disabled and reflects the current status instead.
const STATUS_TO_LABEL: Record<
  DashboardApi20240730.Quotes.Quote['status'],
  string
> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Send signature requests',
  PREPARING_SIGNATURE_REQUEST: 'Preparing signature requests...',
  READY_TO_SIGN: 'Ready to sign',
  ACCEPTED: 'Signed',
  EXECUTED: 'Executed'
}

export const SendSignatureRequestsAction: FC<{
  quote: DashboardApi20240730.Quotes.Quote
}> = ({ quote }) => {
  const { can } = usePermissions()
  const { displayNotification } = useNotifications()

  if (!can('org.quote:update')) {
    return null
  }

  const isPublished = quote.status === 'PUBLISHED'

  if (!isPublished) {
    return (
      <Button
        variant="secondary"
        fullWidth
        disabled
        data-testid="watchtower.notification.sendSignatureRequests"
      >
        {STATUS_TO_LABEL[quote.status]}
      </Button>
    )
  }

  const signers = [...quote.contacts, ...quote.counterSigners]

  return (
    <ModalRequestSignaturesContent
      contacts={signers}
      onSubmit={async emailMessage => {
        // Calls the endpoint directly rather than via a hook; the client
        // still applies the endpoint's `invalidateQueries` for us.
        const response = await dashboard20240730Client.postQuoteReadyToSign({
          id: quote.id,
          body: { emailMessage }
        })

        if (response.error) {
          displayNotification('Failed to send signature requests', {
            type: 'error'
          })
          return false
        }

        displayNotification('Signature requests sent', { type: 'success' })
        return true
      }}
      trigger={
        <Button
          variant="secondary"
          fullWidth
          data-testid="watchtower.notification.sendSignatureRequests"
        >
          Send signature requests
        </Button>
      }
    />
  )
}
